import React, {
  type ComponentPropsWithoutRef,
  type ContextType,
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react';

import { Link, type LinkTo } from '@/components/ui/link';
import { cls } from '@/utils/cls';

const TableContext = createContext<{
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  striped: boolean;
}>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
});

export const Table = forwardRef<HTMLDivElement, any>(function Table(
  {
    bleed = false,
    dense = false,
    grid = false,
    striped = false,
    className,
    children,
    ...props
  }: {
    bleed?: boolean;
    dense?: boolean;
    grid?: boolean;
    striped?: boolean;
  } & ComponentPropsWithoutRef<'div'>,
  ref,
) {
  return (
    <TableContext.Provider
      value={
        { bleed, dense, grid, striped } as ContextType<typeof TableContext>
      }
    >
      <div className="flow-root w-full" ref={ref}>
        {/* Non-scrollable table container */}
        <div className="w-full">
          <div
            {...props}
            className={cls(className, '-mx-[--gutter] whitespace-normal')}
          >
            <div
              className={cls('inline-block min-w-full align-middle', {
                'sm:px-[--gutter]': !bleed,
              })}
            >
              <table className="min-w-full table-fixed text-left text-sm text-gray-800">
                {children}
              </table>
            </div>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
});

export function TableHead({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'thead'>) {
  return (
    <thead {...props} className={cls(className, 'text-gray-600')}>
      {React.Children.map(children, (child) =>
        React.isValidElement(child) && child.type === TableRow
          ? React.cloneElement(
              child as React.ReactElement<{ inHeader?: boolean }>,
              { inHeader: true },
            )
          : child,
      )}
    </thead>
  );
}

export function TableBody(props: ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />;
}

const TableRowContext = createContext<{
  to?: LinkTo;
  target?: string;
  title?: string;
  onClick?: () => void;
}>({
  to: undefined,
  target: undefined,
  title: undefined,
  onClick: undefined,
});

export function TableRow({
  to,
  target,
  title,
  className,
  onClick,
  inHeader,
  ...props
}: {
  to?: LinkTo;
  target?: string;
  title?: string;
  inHeader?: boolean;
} & ComponentPropsWithoutRef<'tr'>) {
  const { striped } = useContext(TableContext);

  return (
    <TableRowContext.Provider
      value={
        { to, target, title, onClick } as ContextType<typeof TableRowContext>
      }
    >
      <tr
        {...props}
        className={cls(className, {
          // Exclude <thead> rows from hover effects
          'hover:bg-gray-200': striped && props?.children && !inHeader,
          'hover:bg-gray-100': !striped && props?.children && !inHeader,
          'even:bg-gray-100': striped, // Striped effect for table body rows only
        })}
      />
    </TableRowContext.Provider>
  );
}

export function TableHeader({
  className,
  wide = false,
  narrow = false,
  ...props
}: ComponentPropsWithoutRef<'th'> & { wide?: boolean; narrow?: boolean }) {
  const { bleed, grid } = useContext(TableContext);

  return (
    <th
      {...props}
      className={cls(
        className,
        'border-b border-b-gray-200 px-4 py-2 text-sm font-medium text-gray-500',
        {
          'w-64 min-w-48 max-w-80 overflow-hidden text-ellipsis whitespace-nowrap':
            wide, // Wide columns with max width
          'w-32 min-w-24 max-w-48 whitespace-normal break-words': narrow, // Narrow columns with meaningful min/max width
          'border-l border-l-gray-300 first:border-l-0': grid,
          'sm:first:pl-1 sm:last:pr-1': !bleed,
        },
      )}
    />
  );
}

export function TableCell({
  className,
  justify = 'left',
  wide = false,
  narrow = false,
  children,
  ...props
}: ComponentPropsWithoutRef<'td'> & {
  justify?: 'left' | 'right' | 'center';
  wide?: boolean;
  narrow?: boolean;
}) {
  const { bleed, dense, grid, striped } = useContext(TableContext);
  const { to, target, title, onClick } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  return (
    <td
      ref={to || onClick ? setCellRef : undefined}
      {...props}
      className={cls(
        className,
        'relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]',
        {
          'text-right': justify === 'right',
          'text-left': justify === 'left',
          'text-center': justify === 'center',
          'border-b border-gray-200': !striped,
          'border-l border-l-gray-200 first:border-l-0': grid,
          'sm:first:pl-1 sm:last:pr-1': !bleed,
          'w-64 min-w-48 max-w-80 overflow-hidden text-ellipsis whitespace-nowrap':
            wide, // Wide columns with max width
          'w-32 min-w-24 max-w-48 whitespace-normal break-words': narrow, // Prevents narrow columns from becoming too small
        },
        dense ? 'py-1.5' : 'py-3.5',
      )}
      onClick={onClick}
    >
      {to ? (
        <Link
          to={to ?? '#'}
          data-row-link
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-none"
        />
      ) : null}
      {children}
    </td>
  );
}
